import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../../../util/api";
import { Box, Chip, Typography } from "@mui/material";
import { notify } from "../../../../util/toast";
import { runApiAction } from "../../../../util/apiFeedback";
import { API } from "../api/endpoints";
import LoadingDots from "../../../../component/common/LoadingDots";
import FormModal from "../../../../component/common/FormModal";
import PermissionTree from "../../../../component/permission/PermissionTree";
import { useMyPermissions } from "../../../../util/useMyPermissions";

const ADMIN_CODE = "GLOBAL_ADMIN";

export default function DepartmentPermissionModal({ open, onClose, deptId, deptName, onSaved }) {
    const { isAdmin } = useMyPermissions();
    const [allPerms, setAllPerms] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [loading, setLoading]   = useState(false);

    /**
     * 부서 권한은 소속 직원 전원에게 퍼지므로 GLOBAL_ADMIN 부여가 특히 위험하다.
     * 전체 관리자가 아니면 잠근다 — 서버 PermissionGrantGuard 가 최종 방어선이고 여기는 사전 안내용.
     */
    const locks = useMemo(() => {
        const m = new Map();
        if (!isAdmin) m.set(ADMIN_CODE, "전체 관리자 권한은 전체 관리자만 부여·회수할 수 있습니다.");
        return m;
    }, [isAdmin]);

    const fetchAllPerms = async () => {
        try {
            const data = await api.get(API.adminPermissions.listAll);
            setAllPerms(Array.isArray(data) ? data : (data?.data || []));
        } catch {
            setAllPerms([]);
            notify("전체 권한 목록 조회 실패", "error");
        }
    };

    const fetchCurrent = async () => {
        if (!deptId) return;
        try {
            const data = await api.get(API.adminPermissions.deptGet(deptId));
            setSelected(new Set(Array.isArray(data) ? data : (data?.data || [])));
        } catch {
            notify("부서 권한 조회 실패", "error");
        }
    };

    /**
     * 두 요청이 <b>둘 다</b> 끝날 때까지 loading 을 유지한다 — 예전엔 권한 목록 조회 구간이
     * loading 밖에 있어서, 목록이 도착하기 전 한 프레임 동안 트리가 빈 상태로 그려졌다
     * ("권한 목록을 불러오지 못했습니다" 가 깜빡임). 트리의 자동 펼침 seed 도 그 빈 목록으로 잡혔다.
     */
    useEffect(() => {
        if (!open) return;
        setLoading(true);
        fetchAllPerms()
            .then(fetchCurrent)
            .finally(() => setLoading(false));
        // eslint-disable-next-line
    }, [open, deptId]);

    const save = () => {
        if (!deptId) return;
        runApiAction({
            request: () => api.put(API.adminPermissions.deptReplace(deptId), {
                codes: Array.from(selected),
            }),
            successMsg: "부서 권한이 저장되었습니다.",
            onSuccess: () => onSaved?.(),
        });
    };

    return (
        <FormModal
            open={open}
            onClose={onClose}
            title="부서 권한 설정"
            subtitle={deptName || "-"}
            maxWidth="md"
            onSave={save}
            footerExtras={
                <Chip
                    label={`${selected.size}개 선택됨`}
                    size="small"
                    sx={{ bgcolor: "#f1f5f9", color: "#475569", fontWeight: 700 }}
                />
            }
        >
            {loading ? (
                <Box sx={{ p: 4, textAlign: "center", color: "#94a3b8" }}><LoadingDots /></Box>
            ) : (
                <>
                    <Typography sx={{ fontSize: 12, color: "#94a3b8", mb: 1.25, lineHeight: 1.5 }}>
                        여기서 준 권한은 이 부서 소속 직원 전원에게 자동으로 적용됩니다.
                        탭 체크로 한꺼번에 주고, 화살표를 열면 조회·관리를 따로 고를 수 있습니다.
                    </Typography>
                    <PermissionTree
                        perms={allPerms}
                        selected={selected}
                        onChange={setSelected}
                        locks={locks}
                    />
                </>
            )}
        </FormModal>
    );
}
